'use client'

import type React from 'react'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'

interface FormSelectOption {
  value: string
  label: string
}

interface FormSelectProps {
  name: string
  options: FormSelectOption[]
  placeholder?: string
  defaultValue?: string
  required?: boolean
  disabled?: boolean
  className?: string
}

/**
 * Thin client wrapper around shadcn Select for use inside server-rendered forms.
 * Radix Select with `name` prop creates a hidden native <input> automatically,
 * so FormClient's `collectSectionValues` picks it up via `[name="..."]`.
 */
const FormSelect: React.FC<FormSelectProps> = ({
  name,
  options,
  placeholder = 'Select an option…',
  defaultValue,
  required,
  disabled,
  className,
}) => {
  return (
    <Select
      name={name}
      required={required}
      defaultValue={defaultValue || undefined}
      disabled={disabled}
    >
      <SelectTrigger
        className={[
          'w-full text-sm',
          disabled ? 'opacity-70 cursor-not-allowed' : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent
        // Render in a portal so it escapes dialog overflow constraints
        position="popper"
        side="bottom"
        align="start"
        className="max-h-[min(300px,40vh)] overflow-y-auto"
      >
        {options.map((opt) => (
          <SelectItem
            key={opt.value}
            value={opt.value}
            className="text-sm py-2.5 sm:py-2 cursor-pointer"
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export default FormSelect
